/**
 * Hikvision catalog — NVRs/DVRs (AcuSense + DeepinMind lineups) and
 * AX PRO / AX wireless intrusion alarm.
 *
 * Every entry below is verified against a primary Hikvision datasheet or
 * the official product page — sources are listed inline above each block.
 *
 * ─── SCHEMA NOTES (for the integrator) ────────────────────────────────
 *  • `AlarmPanelSpec.series` is currently typed as a SATEL-only union
 *    (INTEGRA | INTEGRA Plus | VERSA | PERFECTA | MICRA) — the Hikvision
 *    panels below carry `series: 'AX PRO'` or `'AX'`, which requires
 *    widening that union. Marked with TODO comments on each panel.
 *  • `AlarmPanelSpec.mobileApp` union also needs widening to include
 *    'Hik-Connect' / 'Hik-ProConnect'.
 *  • `AlarmPanelSpec.communicators` already supports 'Ethernet' | 'GSM-LTE'
 *    | 'wireless' — Wi-Fi is encoded as 'wireless' for now.
 *  • `SmokeDetectorSpec.wirelessSystem` and `PanicButtonSpec.wirelessSystem`
 *    unions exclude 'AX PRO' — same union-widening situation applies.
 *
 * Fields are left `undefined` (or omitted) whenever the primary source
 * does not list a value — nothing below is guessed.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const HIKVISION_CATALOG: EquipmentCatalogEntry[] = [
  // NVR + DVR entries moved to hikvisionNvrProCatalog.ts +
  // hikvisionNvrUltraCatalog.ts (deep-scan supersets).
  // ─────────────────────────────────────────────────────────────────────
  // ALARM PANELS — Hikvision AX PRO / AX (wireless hubs)
  // NOTE TO INTEGRATOR: AlarmPanelSpec.series union currently lists only
  // SATEL families ('INTEGRA' | 'INTEGRA Plus' | 'VERSA' | 'PERFECTA' |
  // 'MICRA'). The three panel entries below carry 'AX PRO' / 'AX', which
  // will fail to typecheck until the union is widened. Same goes for
  // mobileApp ('Hik-Connect' / 'Hik-ProConnect').
  // ─────────────────────────────────────────────────────────────────────

  // Source: Hikvision datasheet PDF
  // "_DS-PWA32-HRSG_433868_Wireless-Security-Control-Panel_1.0_20200628"
  // (mirrored at setik.biz, content extracted & verified)
  // https://www.setik.biz/storage/Depliant/DS-PWA32-HGR.pdf
  // NB: DS-PWA32 family = legacy "AX" hub (not AX PRO), 32 wireless zones,
  // single partition. Subsequent gen is AX PRO (DS-PWA64/96).
  {
    id: 'hikvision-ax-pwa32-hgr',
    manufacturer: 'Hikvision',
    model: 'DS-PWA32-HGR',
    category: 'alarm-panel',
    tagline: 'AX 32-zone wireless hub — LAN/Wi-Fi + GPRS, integrated 125 kHz reader',
    verified: true,
    datasheetUrl: 'https://www.setik.biz/storage/Depliant/DS-PWA32-HGR.pdf',
    specs: {
      series: 'AX', // legacy AX (not AX PRO) — single-partition wireless hub
      zonesOnboard: 0,
      zonesMax: 32,
      partitions: 1,
      outputsOnboard: 0,
      outputsMax: 32,
      users: 13,
      grade: 2,
      communicators: ['Ethernet', 'wireless', 'GSM-LTE'],
      mobileApp: 'Hik-Connect',
    },
  },

  // Source: Hikvision official datasheet
  // "DS-PWA64-L-WE_Datasheet_230830" (PDF extracted and verified).
  // https://hikvision.center/wp-content/uploads/2023/12/DS-PWA64-L-WE_Datasheet_230830.pdf
  // Hikvision-hosted regional copy:
  // https://www.hikvision.com/content/dam/hikvision/en/support/regional-materials/bangladesh-/DS-PWA64-L-WE_Datasheet_230830.pdf
  {
    id: 'hikvision-ax-pro-pwa64-l-we',
    manufacturer: 'Hikvision',
    model: 'DS-PWA64-L-WE',
    category: 'alarm-panel',
    tagline: 'AX PRO 64-zone wireless hub — TCP/IP + Wi-Fi + GPRS, 16 areas',
    verified: true,
    datasheetUrl:
      'https://hikvision.center/wp-content/uploads/2023/12/DS-PWA64-L-WE_Datasheet_230830.pdf',
    specs: {
      // TODO(schema): widen series union to include 'AX PRO'.
      series: 'AX PRO',
      zonesOnboard: 0,
      zonesMax: 64,
      partitions: 16, // datasheet "Area: 16"
      users: 32, // 1 installer + 1 admin + 30 normal
      grade: 2,
      communicators: ['Ethernet', 'wireless', 'GSM-LTE'],
      mobileApp: "Hik-Connect", // actually Hik-Connect / Hik-ProConnect
    },
  },

  // Source: Hikvision official datasheet
  // "DS-PWA96-M-WE_Datasheet_20240322" — assets.hikvision.com host (cannot
  // be re-fetched anonymously, but the document is referenced from the
  // official product page below).
  // https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pwa96-m-we/
  {
    id: 'hikvision-ax-pro-pwa96-m-we',
    manufacturer: 'Hikvision',
    model: 'DS-PWA96-M-WE',
    category: 'alarm-panel',
    tagline: 'AX PRO 96-zone wireless hub — TCP/IP + Wi-Fi + 3G/4G, 32 areas',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pwa96-m-we/',
    specs: {
      // TODO(schema): widen series union to include 'AX PRO'.
      series: 'AX PRO',
      zonesOnboard: 0,
      zonesMax: 96, // up to 48 PIRCAMs
      partitions: 32,
      users: 48, // 1 installer + 1 admin + 46 normal
      grade: 2,
      communicators: ['Ethernet', 'wireless', 'GSM-LTE'],
      mobileApp: "Hik-Connect", // actually Hik-Connect / Hik-ProConnect
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // DETECTORS — AX PRO PIR / motion (indoor + outdoor)
  // These map to the existing 'pir-detector' category in
  // DetectionSensorCatalogEntry, NOT to alarm-panel. The full
  // DetectionSensorCatalogEntry shape (rangeM, hfovDeg, pattern, etc.) is
  // already used by the Optex seed data — these entries follow the same
  // shape but I've kept them as comments rather than typed entries so the
  // integrator can pick the exact field names (this file imports
  // EquipmentCatalogEntry but the detection-sensor branch lives in a
  // separate sub-type that I haven't fully resolved here).
  // ─────────────────────────────────────────────────────────────────────

  /*
  // Source: Hikvision official datasheet
  // "DS-PDP15P-EG2-WE_Datasheet" (Hikvision assets, also mirrored at
  // multiple AX PRO distributors). Specs from official product page below.
  // https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pdp15p-eg2-we-b-/
  {
    id: 'hikvision-ax-pro-pdp15p',
    manufacturer: 'Hikvision',
    model: 'DS-PDP15P-EG2-WE',
    category: 'pir-detector',
    tagline: 'AX PRO indoor wireless PIR, 15 m range, 85.9° FoV, 30 kg pet-immune',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pdp15p-eg2-we-b-/',
    rangeM: 15,
    hfovDeg: 85.9,
    pattern: 'wide-angle',
    petImmunityKg: 30,
    mountingHeightM: '1.8–2.4',
    indoor: true,
    wirelessSystem: 'AX PRO',
    batteryYears: 5,
  },
  // Source: Hikvision official datasheet
  // "DS-PDC10AM-EG2-WE_Datasheet" — assets.hikvision.com (referenced from
  // the official product page).
  // https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pdc10am-eg2-we/
  {
    id: 'hikvision-ax-pro-pdc10am',
    manufacturer: 'Hikvision',
    model: 'DS-PDC10AM-EG2-WE',
    category: 'pir-detector',
    tagline: 'AX PRO outdoor wireless PIR curtain, 10 m wall, IP65, active anti-mask',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pdc10am-eg2-we/',
    rangeM: 10,
    pattern: 'curtain',
    petImmunityKg: 12,
    ipRating: 'IP65',
    wirelessSystem: 'AX PRO',
    antiMask: true,
  },
  */

  // ─────────────────────────────────────────────────────────────────────
  // MAGNETIC CONTACT — AX PRO
  // ─────────────────────────────────────────────────────────────────────

  // Source: Hikvision official product page + datasheet (officially listed
  // by Hikvision as DS-PDMC-EG2-WE / DS-PDMCS-EG2-WE family). Specs
  // confirmed against the global product page.
  // https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pdmcs-eg2-we/
  {
    id: 'hikvision-ax-pro-pdmc-eg2-we',
    manufacturer: 'Hikvision',
    model: 'DS-PDMC-EG2-WE',
    category: 'magnetic-contact',
    tagline: 'AX PRO wireless door/window contact, 868 MHz, 1600 m open-air',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pdmcs-eg2-we/',
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
      dimensionsMm: '22.5 × 103 × 13 (sensor) / 13 × 34.4 × 11.4 (magnet)',
      gateRated: false,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // SMOKE DETECTOR — AX PRO
  // ─────────────────────────────────────────────────────────────────────

  // Source: Hikvision official datasheet
  // "DS-PDSMK-S-WE_Datasheet" (assets.hikvision.com), referenced from the
  // EU product page below.
  // https://www.hikvision.com/europe/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pdsmk-s-we/
  {
    id: 'hikvision-ax-pro-pdsmk-s-we',
    manufacturer: 'Hikvision',
    model: 'DS-PDSMK-S-WE',
    category: 'smoke-detector',
    tagline: 'AX PRO wireless photoelectric smoke detector, EN 14604, 10 yr battery',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/europe/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pdsmk-s-we/',
    specs: {
      detection: ['smoke-optical'],
      standard: ['EN 14604'],
      standalone: false, // wireless-bound to AX PRO hub
      batteryYears: 10,
      wirelessSystem: 'AX PRO',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // GLASS-BREAK DETECTOR — AX PRO
  // ─────────────────────────────────────────────────────────────────────

  // Source: Hikvision official product page (datasheet on assets.hikvision.com)
  // https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pdbg8-eg2-we/
  {
    id: 'hikvision-ax-pro-pdbg8-eg2-we',
    manufacturer: 'Hikvision',
    model: 'DS-PDBG8-EG2-WE',
    category: 'glass-break',
    tagline: 'AX PRO wireless omni glass-break detector, 8 m / 120°, 868 MHz',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-pdbg8-eg2-we/',
    specs: {
      method: 'acoustic-single', // omnidirectional acoustic sensor (Hikvision lists
      // it as a single high-quality microphone with multi-band digital processing).
      glassTypes: ['plain', 'tempered', 'laminated'],
      rangeM: 8,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // SIRENS — AX PRO indoor / outdoor wireless sounders
  // ─────────────────────────────────────────────────────────────────────

  // Source: Hikvision official product page (DS-PS1-E-WB datasheet hosted
  // on assets.hikvision.com — anonymous fetch blocked, specs verified from
  // the Hikvision Global product page + distributor mirrors).
  // https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-ps1-e-wb/
  {
    id: 'hikvision-ax-pro-ps1-e-wb',
    manufacturer: 'Hikvision',
    model: 'DS-PS1-E-WB',
    category: 'outdoor-siren',
    tagline: 'AX PRO outdoor wireless sounder, 110 dB, IP65, 433 MHz two-way',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-ps1-e-wb/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 110,
      grade: 2,
      batteryBackup: 'built-in',
      ipRating: 'IP65',
    },
  },

  // Source: Hikvision official product page (DS-PS1-I-WE datasheet hosted
  // on assets.hikvision.com — see note above).
  // https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-ps1-i-we/
  {
    id: 'hikvision-ax-pro-ps1-i-we',
    manufacturer: 'Hikvision',
    model: 'DS-PS1-I-WE',
    category: 'indoor-sounder',
    tagline: 'AX PRO indoor wireless siren, 110 dB, 868 MHz, CR123A battery',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/Alarm-Products/wireless-intrusion-alarm/ax-pro/ds-ps1-i-we/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 110,
      grade: 2,
      batteryBackup: 'built-in',
    },
  },
];
